// Shared multi-select checkbox list for player-side game control screens.

import React, { useCallback } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from '@/components/ui/field';
import { cn } from '@/utilities/cn';

export type CheckboxOption = {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
};

interface PlayingCheckboxListProps {
  options: CheckboxOption[];
  selectedValues: string[];
  onSelectedValuesChange: (values: string[]) => void;
  /** Cap on how many options can be selected at once. Omit for no cap. */
  maxSelections?: number;
  columns?: 1 | 2;
}

const gridColsClass = (columns: 1 | 2): string =>
  columns === 2 ? 'grid-cols-2' : 'grid-cols-1';

const PlayingCheckboxList: React.FC<PlayingCheckboxListProps> = ({
  options,
  selectedValues,
  onSelectedValuesChange,
  maxSelections,
  columns = 1,
}) => {
  const atCap =
    maxSelections !== undefined && selectedValues.length >= maxSelections;

  const handleToggle = useCallback(
    (value: string) => {
      if (selectedValues.includes(value)) {
        onSelectedValuesChange(selectedValues.filter((v) => v !== value));
        return;
      }
      if (
        maxSelections !== undefined &&
        selectedValues.length >= maxSelections
      ) {
        return;
      }
      onSelectedValuesChange([...selectedValues, value]);
    },
    [selectedValues, onSelectedValuesChange, maxSelections]
  );

  if (options.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-2.5">
        <Empty className="min-h-44 rounded-lg border border-solid bg-card py-8 shadow-sm">
          <EmptyHeader>
            <EmptyTitle>No choices available</EmptyTitle>
            <EmptyDescription>
              There aren&apos;t any options to pick right now. Hang tight.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  // NOTE: native overflow div, NOT Radix ScrollArea — matches PlayingButtonList
  // so touch scrolling works inside a vaul Drawer. See that file for context.
  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      data-vaul-no-drag
    >
      <div
        className={cn(
          'grid w-full gap-2 px-4 pb-4 pt-2.5',
          gridColsClass(columns)
        )}
      >
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const isDisabled = !isSelected && atCap;
          const controlId = `playing-checkbox-${option.value}`;

          return (
            <FieldLabel
              key={option.value}
              htmlFor={controlId}
              className={cn(
                'cursor-pointer rounded-lg border bg-card p-3 transition-colors',
                isSelected
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/60',
                isDisabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <Field orientation="horizontal" data-disabled={isDisabled}>
                <Checkbox
                  id={controlId}
                  checked={isSelected}
                  disabled={isDisabled}
                  onCheckedChange={() => handleToggle(option.value)}
                />
                <FieldContent>
                  <FieldTitle>{option.label}</FieldTitle>
                  {option.description && (
                    <FieldDescription>{option.description}</FieldDescription>
                  )}
                </FieldContent>
              </Field>
            </FieldLabel>
          );
        })}
      </div>
    </div>
  );
};

export default PlayingCheckboxList;
